import { inject, Injectable } from '@angular/core';
import { getEntityType } from '@datorama/akita';
import { memo } from 'helpful-decorators';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { FormControlConstants } from '@shared/constants/form-control.constants';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { DriverType } from '@shared/services/gql.service';

import { ActivityQuery } from '../../../state/activity/activity.query';
import { CategoryQuery, FullActivity, FullCategory } from '../../../state/category/category.query';
import { ForecastSettingsQuery } from '../../../state/settings/forecast-settings.query';
import { ForecastSettingsState } from '../../../state/settings/forecast-settings.store';

@Injectable({
  providedIn: 'root',
})
export class ForecastTableGridCategoryCheckService {
  private activityQuery = inject(ActivityQuery);
  private categoryQuery = inject(CategoryQuery);
  private forecastSettingsQuery = inject(ForecastSettingsQuery);

  readonly formControlConstants = FormControlConstants;

  checkIfActivityHasError(
    activityId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const activity = this.activityQuery.getEntity(activityId);

    if (!activity) {
      return true;
    }

    const settingsId = activity.primary_settings_id;

    if (!settingsId) {
      return true;
    }

    return this.checkIfSettingHasError(
      settingsId,
      isPatientDriverAvailable,
      isSiteDriverAvailable,
      isCustomDriverAvailable
    );
  }

  checkIfAnyChildActivitiesHasError(
    categoryId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    if (!category) {
      return true;
    }

    const categoryOverride = this.forecastSettingsQuery.getEntity(
      category.primary_settings_id
    )?.override;

    let activities = category.activity_ids.map((activityId) =>
      this.activityQuery.getEntity(activityId)
    );

    if (categoryOverride) {
      activities = activities.filter(
        (activity) => this.forecastSettingsQuery.getEntity(activity?.primary_settings_id)?.override
      );
    }

    if (activities.length) {
      return activities.some((activity) => {
        if (!activity) {
          return true;
        }

        return this.checkIfActivityHasError(
          activity.id,
          isPatientDriverAvailable,
          isSiteDriverAvailable,
          isCustomDriverAvailable
        );
      });
    }

    return false;
  }

  checkIfAnyChildCategoriesHasError(
    categoryId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    if (!category) {
      return true;
    }

    if (category.sub_category_ids.length) {
      return category.sub_category_ids.some((subCategoryId) => {
        return (
          this.checkIfCategoryHasError(
            subCategoryId,
            isPatientDriverAvailable,
            isSiteDriverAvailable,
            isCustomDriverAvailable
          ) ||
          this.checkIfAnyChildCategoriesHasError(
            subCategoryId,
            isPatientDriverAvailable,
            isSiteDriverAvailable,
            isCustomDriverAvailable
          ) ||
          this.checkIfAnyChildActivitiesHasError(
            subCategoryId,
            isPatientDriverAvailable,
            isSiteDriverAvailable,
            isCustomDriverAvailable
          )
        );
      });
    }

    return false;
  }

  checkIfCategoryHasError(
    categoryId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    if (!category) {
      return true;
    }

    const settingsId = category.primary_settings_id;

    if (!settingsId) {
      return true;
    }

    const settings = this.forecastSettingsQuery.getEntity(settingsId);

    if (!settings) {
      return true;
    }

    if (!settings.override) {
      return false;
    }

    return category.parent_category_id
      ? false
      : this.checkIfSettingHasError(
          settingsId,
          isPatientDriverAvailable,
          isSiteDriverAvailable,
          isCustomDriverAvailable
        );
  }

  checkIfSettingHasError(
    settingsId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const settings = this.forecastSettingsQuery.getEntity(settingsId);

    if (!settings) {
      return true;
    }

    if (!settings.driver) {
      return true;
    }

    switch (settings.driver) {
      case DriverType.DRIVER_CUSTOM:
        return !(isCustomDriverAvailable && settings.driver_setting_id);
      case DriverType.DRIVER_PATIENT:
        return !(isPatientDriverAvailable && settings.driver_setting_id);
      case DriverType.DRIVER_SITE:
        return !(isSiteDriverAvailable && settings.driver_setting_id);
      case DriverType.DRIVER_TIME:
        return !(
          settings.forecast_method &&
          (settings.period_start_milestone_id || settings.period_start_date) &&
          (settings.period_end_milestone_id || settings.period_end_date)
        );
      default:
        return true;
    }
  }

  getActivityType(activity: unknown): FullActivity {
    return activity as FullActivity;
  }

  getAllSubSettingIDs(categoryId: string): string[] {
    const settingsIds: string[] = [];
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    // Get activity ids
    const activitySettingIds = this.activityQuery
      .getAll({
        filterBy: (activity) => {
          return (
            this.forecastSettingsQuery.getEntity(activity.primary_settings_id)?.override ===
              false && category?.activity_ids?.includes(activity.id)
          );
        },
      })
      .map((activity) => activity.primary_settings_id);

    // Add activity ids
    settingsIds.push(...activitySettingIds);

    // Add sub setting ids
    this.categoryQuery
      .getAll({
        filterBy: (subCategory) => {
          return (
            this.forecastSettingsQuery.getEntity(subCategory.primary_settings_id)?.override ===
              false && subCategory.parent_category_id === categoryId
          );
        },
      })
      .forEach((subCategory) => {
        settingsIds.push(subCategory.primary_settings_id);
        settingsIds.push(...this.getAllSubSettingIDs(subCategory.id));
      });

    return settingsIds;
  }

  getCategoryType(category: unknown): FullCategory {
    return category as FullCategory;
  }

  @memo()
  isActivityUnforecasted(activityId: string): Observable<boolean> {
    return this.activityQuery.selectEntity(activityId).pipe(
      switchMap((activity) => {
        return combineLatest([
          this.forecastSettingsQuery.selectEntity(activity?.primary_settings_id),
          this.forecastSettingsQuery.ui.selectEntity(activity?.primary_settings_id),
        ]).pipe(
          map(([settings, ui]) => {
            const categoryOverride = !!this.forecastSettingsQuery.getEntity(
              this.categoryQuery.getEntity(activity?.category_id)?.primary_settings_id
            )?.override;

            return ((settings?.override || !categoryOverride) && ui?.unforecasted) || false;
          })
        );
      })
    );
  }

  isCategoryIndeterminate(categoryId: string): boolean {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    if (category) {
      const numActivityCheckedCount = category.activity_checked_count;

      return numActivityCheckedCount ? numActivityCheckedCount > 0 : false;
    }

    return false;
  }

  isCategoryNeedUnforecastedFlag(
    categoryId: string,
    isPatientDriverAvailable: boolean,
    isSiteDriverAvailable: boolean,
    isCustomDriverAvailable: boolean
  ): boolean {
    const entity = this.categoryQuery.getEntity(categoryId);
    const category = this.getCategoryType(entity);

    if (!category) {
      return true;
    }

    const hasError = this.checkIfCategoryHasError(
      categoryId,
      isPatientDriverAvailable,
      isSiteDriverAvailable,
      isCustomDriverAvailable
    );

    if (hasError) {
      return true;
    }

    const isCategoryOpen = !!this.categoryQuery.ui.getEntity(categoryId)?.isOpen;
    const hasActivityChecked = !!category.activity_checked_count;

    if (!isCategoryOpen && !hasActivityChecked) {
      return hasActivityChecked;
    }

    return (
      this.checkIfAnyChildCategoriesHasError(
        categoryId,
        isPatientDriverAvailable,
        isSiteDriverAvailable,
        isCustomDriverAvailable
      ) ||
      this.checkIfAnyChildActivitiesHasError(
        categoryId,
        isPatientDriverAvailable,
        isSiteDriverAvailable,
        isCustomDriverAvailable
      )
    );
  }

  @memo()
  isCategoryUnforecasted(primarySettingsId: string): Observable<boolean> {
    return this.forecastSettingsQuery.ui.selectEntity(
      primarySettingsId,
      'unforecasted'
    ) as Observable<boolean>;
  }

  isDriverSettingIdUnfilled(
    settings: getEntityType<ForecastSettingsState> | undefined,
    driverType: DriverType
  ): boolean {
    return !!(settings?.driver && settings?.driver === driverType && !settings.driver_setting_id);
  }

  isDriverTimeCustomPeriodFilled(
    settings: getEntityType<ForecastSettingsState> | undefined
  ): boolean {
    return !!(
      (settings?.period_start_milestone_id || settings?.period_start_date) &&
      (settings?.period_end_milestone_id || settings?.period_end_date)
    );
  }

  isDriverTimeUnfilled(settings: getEntityType<ForecastSettingsState> | undefined): boolean {
    return (
      settings?.driver === DriverType.DRIVER_TIME &&
      (!settings?.forecast_method || !this.isDriverTimeCustomPeriodFilled(settings))
    );
  }

  @memo()
  isShowError(primarySettingsId: string): Observable<boolean> {
    return this.forecastSettingsQuery.ui.selectEntity(
      primarySettingsId,
      'showError'
    ) as Observable<boolean>;
  }

  @memo()
  isShowErrorMessage(primarySettingsId: string): Observable<string> {
    return this.forecastSettingsQuery.ui.selectEntity(
      primarySettingsId,
      'errorMessage'
    ) as Observable<string>;
  }

  pageLockedTooltipText(isActionDisabled: boolean): string {
    return isActionDisabled ? MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE : '';
  }

  tooltipText(
    isForecastFinalizedOrIsClosedMonthsProcessing: boolean,
    userHasModifyPermissions: boolean
  ): string {
    if (isForecastFinalizedOrIsClosedMonthsProcessing) {
      return MessagesConstants.PAGE_LOCKED_FOR_PERIOD_CLOSE;
    }

    return userHasModifyPermissions ? '' : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  }
}
